import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { LocationNomenclator } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface ProvincesQueryParams {
  all_data?: boolean
  countryId?: number
  search?: string
}

// Create a unique, descriptive key for this query
const PROVINCES_QUERY_KEY = 'provinces'

interface ProvincesResponse {
  items: LocationNomenclator[]
  totalItems: number
  currentPage: number
  totalPages: number
}

/**
 * Fetch provinces from the API
 * Separating API call logic from the hook for better testability
 */
const fetchProvinces = async (
  params?: ProvincesQueryParams
): Promise<ProvincesResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/invoice/public/provinces', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching provinces
 *
 * @param params - Query parameters for filtering provinces
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useProvincesQuery = ({
  params,
  options,
}: {
  params: ProvincesQueryParams
  options?: Partial<UseQueryOptions<ProvincesResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<ProvincesResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [PROVINCES_QUERY_KEY, params],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchProvinces(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes (location data doesn't change often)

    // Apply any custom options
    ...options,
  })

  return {
    provinces: queryResult.data?.items || [],
    isLoadingProvinces: queryResult.isLoading,
    isErrorProvinces: queryResult.isError,
    errorProvinces: queryResult.error,
    refetchProvinces: queryResult.refetch,
    isFetchingProvinces: queryResult.isFetching,
  }
}

/**
 * Helper function to get the provinces query key
 * Useful for cache invalidation or prefetching
 */
export const getProvincesQueryKey = (params?: ProvincesQueryParams) => [
  PROVINCES_QUERY_KEY,
  params,
]
